import React, { useState, useEffect, useRef } from 'react';
import { Music, FileText, ImageIcon, FolderOpen, Eye, MessageSquare, Palette, Sparkles, ChevronDown, ChevronUp, Plus, Heart, MessageCircle, Trash2, Video, Play, PlaySquare, Scissors, Layers } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Format speaker labels (convert spk_0 to Speaker 1, etc.)
const formatSpeaker = (speaker: string) => {
  if (!speaker) return 'Speaker 1';
  if (speaker.startsWith('spk_')) {
    const num = parseInt(speaker.replace('spk_', '')) + 1;
    return `Speaker ${num}`;
  }
  return speaker;
};

// Format time (show minutes after 60s)
const formatTime = (seconds: number) => {
  if (!seconds && seconds !== 0) return '';
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(0).padStart(2, '0');
  return `${minutes}:${secs}`;
};

interface ProjectFile {
  id: string;
  name: string;
  displayName?: string; // Name with green screen indicator
  fileType?: 'video' | 'audio' | 'image' | 'srt';
  thumbnailUrl?: string;
  duration: number;
  analysisData?: {
    transcript?: any;
    scenes?: any[];
    background?: string;
  };
  processingStatus?: 'starting' | 'processing' | 'succeeded' | 'failed';
  processingJobId?: string;
  analysisStatus?: 'starting' | 'processing' | 'completed' | 'failed';
  analysisJobId?: string;
  layerType?: 'main' | 'background' | 'audio' | 'mask';
  linkedAudioId?: string;
  linkedVideoId?: string;
  linkedMaskId?: string; // For luma matte pairs
  linkedPairId?: string;
  hasGreenScreen?: boolean;
  greenScreenUrl?: string;
  greenScreenKey?: string;
  greenScreenType?: 'green-screen' | 'foreground-mask' | 'alpha-mask';
  hasAudio?: boolean;
}

interface FileItemProps {
  file: ProjectFile;
  isSelected: boolean;
  onSelect: () => void;
  onDragStart: () => void;
  onAddToTimeline?: () => void;
  onAddToTimelineWithOptions?: (options: { includeMask?: boolean; videoOnly?: boolean }) => void;
  viewMode?: 'grid' | 'list';
  onInsertText?: (text: string, file?: ProjectFile, startTime?: number, endTime?: number, referenceText?: string) => void;
  onFileRemove?: (fileId: string) => void;
  onTranscriptEdit?: (fileId: string, segmentIndex: number, newText: string) => void;
  onSpeakerEdit?: (fileId: string, segmentIndex: number, newSpeaker: string) => void;
}

export const FileItem: React.FC<FileItemProps> = ({
  file,
  isSelected,
  onSelect,
  onDragStart,
  onAddToTimeline,
  onAddToTimelineWithOptions,
  viewMode = 'grid',
  onInsertText,
  onFileRemove,
  onTranscriptEdit,
  onSpeakerEdit,
}) => {
  // Debug: Component loading and updates
  React.useEffect(() => {
    console.log('🔥 FileItem component loaded with file:', file.name);
  }, []);

  // Debug: Track speaker changes
  React.useEffect(() => {
    const speakers = file.analysisData?.transcript?.segments?.map((s: any) => s.speaker);
    console.log(`📢 FileItem "${file.name}" speakers:`, speakers);
  }, [file.analysisData?.transcript?.segments]);

  const [showPreview, setShowPreview] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [generatedThumbnail, setGeneratedThumbnail] = useState<string | null>(null);
  const thumbnailAttempted = useRef(false);

  // Generate thumbnail on the fly for videos without one (e.g. imported casting clips)
  useEffect(() => {
    if (file.thumbnailUrl || file.fileType !== 'video' || !file.s3Url || thumbnailAttempted.current) return;
    thumbnailAttempted.current = true;

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true;

    const cleanup = () => { video.removeAttribute('src'); video.load(); };
    const timeout = setTimeout(() => { cleanup(); }, 10000); // 10s timeout

    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1, video.duration * 0.1);
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        const isPortrait = video.videoHeight > video.videoWidth;
        canvas.width = isPortrait ? 180 : 320;
        canvas.height = isPortrait ? 320 : 180;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          setGeneratedThumbnail(canvas.toDataURL('image/jpeg', 0.7));
        }
      } catch (e) {
        // Canvas tainted or other error — leave as icon
      }
      clearTimeout(timeout);
      cleanup();
    };
    video.onerror = () => { clearTimeout(timeout); cleanup(); };

    video.src = file.s3Url;

    return () => { clearTimeout(timeout); cleanup(); };
  }, [file.thumbnailUrl, file.fileType, file.s3Url]);
  // Insert video segment with auto-generated enhancement prompt
  const handleInsertVideoSegment = (segment: any) => {
    if (onInsertText && segment) {
      // Generate auto-enhancement prompt
      const startTime = segment.start_time;
      const endTime = segment.end_time;
      const text = segment.text;
      const speaker = formatSpeaker(segment.speaker);
      
      // Create prompt without quoted text for LLM, but show text separately for user reference
      // Check if this is a green screen video and add chromaKey instruction
      const isGreenScreen = (file as any).hasGreenScreen;
      const enhancementPrompt = isGreenScreen 
        ? `chromaKey #97eca9`
        : '';
      
      // Insert only the enhancement prompt (trim instruction) - reference text is shown separately in UI
      onInsertText(
        enhancementPrompt,
        file, 
        startTime, 
        endTime,
        text // Pass the original transcript text as reference
      );
    }
  };
  
  const renderThumbnail = () => {
    // Explicit icon markers
    if (file.thumbnailUrl === 'AUDIO_ICON') {
      return (
        <div className="w-full aspect-video bg-muted rounded flex items-center justify-center">
          <Music className="w-6 h-6 text-muted-foreground" />
        </div>
      );
    }

    if (file.thumbnailUrl === 'SRT_ICON') {
      return (
        <div className="w-full aspect-video bg-muted rounded flex items-center justify-center">
          <FileText className="w-6 h-6 text-muted-foreground" />
        </div>
      );
    }

    if (file.thumbnailUrl === 'VIDEO_ICON') {
      return (
        <div className="w-full aspect-video bg-muted rounded flex items-center justify-center">
          <Video className="w-6 h-6 text-muted-foreground" />
        </div>
      );
    }

    // No thumbnail - use generated thumbnail or appropriate icon based on file type
    if (!file.thumbnailUrl) {
      // Use on-the-fly generated thumbnail if available
      if (generatedThumbnail) {
        return (
          <div className="w-full aspect-video bg-muted rounded overflow-hidden flex items-center justify-center">
            <img src={generatedThumbnail} alt={file.name} className="max-w-full max-h-full object-contain" />
          </div>
        );
      }

      // Check file type or AI asset type
      const isVideo = file.fileType === 'video' || (file as any).aiAssetType === 'video';
      const isAudio = file.fileType === 'audio' || (file as any).aiAssetType === 'music';
      const isImage = file.fileType === 'image' || (file as any).aiAssetType === 'image';

      return (
        <div className="w-full aspect-video bg-muted rounded flex items-center justify-center">
          {isVideo ? (
            <Video className="w-6 h-6 text-muted-foreground" />
          ) : isAudio ? (
            <Music className="w-6 h-6 text-muted-foreground" />
          ) : isImage ? (
            <ImageIcon className="w-6 h-6 text-muted-foreground" />
          ) : (
            <Video className="w-6 h-6 text-muted-foreground" />
          )}
        </div>
      );
    }

    if (file.fileType === 'image') {
      return (
        <div className="w-full aspect-video bg-muted rounded overflow-hidden flex items-center justify-center">
          <img
            src={file.thumbnailUrl}
            alt={file.name}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      );
    }

    if (file.thumbnailUrl.startsWith('blob:') || file.thumbnailUrl.startsWith('data:') || file.thumbnailUrl.startsWith('http')) {
      return (
        <div className="w-full aspect-video bg-muted rounded overflow-hidden flex items-center justify-center">
          <img
            src={file.thumbnailUrl}
            alt={file.name}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      );
    }

    return (
      <div className="w-full h-20 bg-muted rounded flex items-center justify-center">
        <FolderOpen className="w-6 h-6 text-muted-foreground" />
      </div>
    );
  };

  const hasAnalysis = file.analysisData && (
    file.analysisData.transcript ||
    file.analysisData.background
  );

  if (viewMode === 'list') {
    return (
      <>
        <div
          className={`relative group border rounded-lg overflow-hidden transition-all ${
            isSelected
              ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
              : 'border-border hover:border-primary/50 bg-card'
          }`}
        >
          <div
            draggable={!!onAddToTimeline}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'copy';
              e.dataTransfer.setData('fileId', file.id);
              onDragStart();
            }}
            onClick={() => { if (!onAddToTimeline) onSelect(); }}
            className={`${onAddToTimeline ? 'cursor-move' : 'cursor-pointer'} flex items-center gap-3 p-3`}
          >
          {/* Selection Checkbox */}
          <Checkbox
            checked={isSelected}
            onCheckedChange={onSelect}
            onClick={(e) => e.stopPropagation()}
          />

          {/* Thumbnail */}
          <div className="w-20 h-12 flex-shrink-0">
            {renderThumbnail()}
          </div>

          {/* File Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium truncate" title={file.displayName || file.name}>
                {file.displayName || file.name}
              </p>
              {file.hasGreenScreen && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                  🟢 Green Screen
                </span>
              )}
              {(file as any).linkedMaskId && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                  ◐ Luma Matte
                </span>
              )}
              {file.layerType && file.layerType !== 'main' && file.layerType !== 'mask' && (
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                  file.layerType === 'background' ? 'bg-purple-100 text-purple-700' :
                  file.layerType === 'audio' ? 'bg-blue-100 text-blue-700' : ''
                }`}>
                  {file.layerType === 'background' ? '🎨' : '🎵'} {file.layerType}
                </span>
              )}
              {(file.linkedAudioId || file.linkedVideoId) && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                  🔗 Linked
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              {file.duration > 0 ? `${Math.round(file.duration)}s • ` : ''}{file.fileType}
              {(file as any).aspectRatio && ` • ${(file as any).aspectRatio === '9:16' ? '📱 Portrait' : '🖥️ Landscape'}`}
            </p>
            
            {/* Processing Status */}
            {file.processingStatus && (
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center gap-1">
                  {file.processingStatus === 'starting' && (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border border-blue-300 border-t-blue-600" />
                      <span className="text-xs text-blue-600 font-medium">Starting background removal...</span>
                    </>
                  )}
                  {file.processingStatus === 'processing' && (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border border-green-300 border-t-green-600" />
                      <span className="text-xs text-green-600 font-medium">Removing background (may take 5-15 min)...</span>
                    </>
                  )}
                  {file.processingStatus === 'failed' && (
                    <>
                      <div className="rounded-full h-3 w-3 bg-red-500" />
                      <span className="text-xs text-red-600 font-medium">Processing Failed</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Analysis Status */}
            {(() => { 
              if (file.analysisStatus) {
                console.log('📊 RENDERING Analysis Status:', file.name, file.analysisStatus, file.analysisJobId);
              }
            })()}
            
            
            {file.analysisStatus && (
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center gap-1">
                  {file.analysisStatus === 'starting' && (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border border-purple-300 border-t-purple-600" />
                      <span className="text-xs text-purple-600 font-medium">Starting analysis...</span>
                    </>
                  )}
                  {file.analysisStatus === 'processing' && (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border border-yellow-300 border-t-yellow-600" />
                      <span className="text-xs text-yellow-600 font-medium">Analyzing video (may take up to 30 min)...</span>
                    </>
                  )}
                  {file.analysisStatus === 'completed' && (
                    <>
                      <div className="rounded-full h-3 w-3 bg-green-500" />
                      <span className="text-xs text-green-600 font-medium">Analysis complete</span>
                    </>
                  )}
                  {file.analysisStatus === 'failed' && (
                    <>
                      <div className="rounded-full h-3 w-3 bg-red-500" />
                      <span className="text-xs text-red-600 font-medium">Analysis failed</span>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Inline Analysis Data */}
            {hasAnalysis && (
              <div className="space-y-1.5 text-xs">
                {file.analysisData?.background && (
                  <div className="flex items-start gap-2">
                    <Palette className="w-3 h-3 text-purple-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-muted-foreground text-xs">BG: </span>
                      <span className="text-foreground text-xs">
                        {typeof file.analysisData.background === 'object' 
                          ? `${file.analysisData.background.type}`
                          : file.analysisData.background}
                      </span>
                    </div>
                  </div>
                )}
                {file.analysisData?.transcript && (
                  <div className="flex items-start gap-2">
                    <MessageSquare className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-muted-foreground text-xs">Transcript: </span>
                      <span className="text-foreground text-xs">
                        {(() => {
                          const transcript = file.analysisData.transcript;
                          if (transcript?.segments?.length > 0) {
                            const speakers = transcript.speaker_count || 1;
                            return `${speakers} speaker${speakers > 1 ? 's' : ''}, ${transcript.segments.length} segments`;
                          }
                          if (transcript?.full_text) return 'Full text available';
                          if (typeof transcript === 'string') return 'Text available';
                          return 'Available';
                        })()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Casting Data - Likes and Comments (moved to separate column) */}
          {(file.liked || (file.comments && file.comments.length > 0)) && (
            <div className="flex items-center gap-2 text-xs flex-shrink-0">
              {file.liked && (
                <div className="flex items-center gap-1">
                  <Heart className="w-3 h-3 text-red-500 fill-red-500" />
                  <span className="text-red-600 font-medium sr-only">Liked</span>
                </div>
              )}
              {file.comments && file.comments.length > 0 && (
                <button 
                  className="flex items-center gap-1 hover:bg-blue-50 px-1 py-0.5 rounded transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowComments(true);
                  }}
                  title={`${file.comments.length} comment${file.comments.length > 1 ? 's' : ''}`}
                >
                  <MessageCircle className="w-3 h-3 text-blue-500" />
                  <span className="text-blue-600 font-medium">{file.comments.length}</span>
                </button>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {onAddToTimeline && (() => {
              const hasTranscript = hasAnalysis && file.analysisData?.transcript?.segments?.length > 0;
              const hasLumaMask = !!(file as any).linkedMaskId;
              const hasGreenScreen = !!file.hasGreenScreen;
              const showDropdown = hasTranscript || hasLumaMask || hasGreenScreen;

              if (showDropdown) {
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="default"
                        className="h-7 px-2 text-xs"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add
                        <ChevronDown className="h-2.5 w-2.5 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {/* Green screen option */}
                      {hasGreenScreen && (
                        <DropdownMenuItem onClick={() => onAddToTimeline()}>
                          <span className="text-green-600 mr-2">🟢</span>
                          With Green Screen
                        </DropdownMenuItem>
                      )}
                      {/* Luma matte option */}
                      {hasLumaMask && (
                        <DropdownMenuItem onClick={() => {
                          if (onAddToTimelineWithOptions) {
                            onAddToTimelineWithOptions({ includeMask: true });
                          } else {
                            onAddToTimeline();
                          }
                        }}>
                          <Layers className="h-3.5 w-3.5 mr-2 text-purple-600" />
                          With Luma Matte
                        </DropdownMenuItem>
                      )}
                      {/* Video only option */}
                      {(hasLumaMask || hasGreenScreen) && (
                        <DropdownMenuItem onClick={() => {
                          if (onAddToTimelineWithOptions) {
                            onAddToTimelineWithOptions({ videoOnly: true });
                          } else {
                            onAddToTimeline();
                          }
                        }}>
                          <PlaySquare className="h-3.5 w-3.5 mr-2" />
                          Original Video Only
                        </DropdownMenuItem>
                      )}
                      {/* Standard full video option when no processing */}
                      {!hasLumaMask && !hasGreenScreen && (
                        <DropdownMenuItem onClick={() => onAddToTimeline()}>
                          <PlaySquare className="h-3.5 w-3.5 mr-2" />
                          Full Video
                        </DropdownMenuItem>
                      )}
                      {hasTranscript && (
                        <DropdownMenuItem onClick={() => setShowPreview(true)}>
                          <Scissors className="h-3.5 w-3.5 mr-2" />
                          Select Sections...
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              return (
                <Button
                  size="sm"
                  variant="default"
                  className="h-7 px-2 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddToTimeline();
                  }}
                  title="Add to timeline"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              );
            })()}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={(e) => {
                e.stopPropagation();
                setShowPreview(true);
              }}
            >
              <Eye className="h-3.5 w-3.5" />
            </Button>
            {hasAnalysis && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAnalysis(true);
                }}
              >
                <Sparkles className="h-3.5 w-3.5" />
              </Button>
            )}
            {file.analysisData?.transcript && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                title={isExpanded ? "Collapse transcript" : "Expand transcript"}
              >
                {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </Button>
            )}
          </div>
          </div>

          {/* Expanded Transcript Section */}
          {isExpanded && file.analysisData?.transcript && (
            <div className="border-t border-border p-4 bg-muted/30">
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {file.analysisData.transcript?.segments ? (
                  <>
                    {file.analysisData.transcript.segments.map((segment: any, index: number) => (
                      <div key={`${index}-${segment.speaker}`} className="p-2 bg-background rounded-lg group">
                        <div className="flex items-center justify-between mb-1">
                          {onSpeakerEdit && (file.analysisData?.transcript?.speaker_count || 0) > 1 ? (
                            <Select
                              value={segment.speaker || 'spk_0'}
                              onValueChange={(value) => onSpeakerEdit(file.id, index, value)}
                            >
                              <SelectTrigger className="h-6 w-auto min-w-0 gap-1 px-1.5 py-0 text-xs font-semibold text-primary border-transparent hover:border-border bg-transparent">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Array.from({ length: file.analysisData.transcript.speaker_count }, (_, i) => (
                                  <SelectItem key={i} value={`spk_${i}`} className="text-xs">
                                    Speaker {i + 1}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-xs font-semibold text-primary">
                              {formatSpeaker(segment.speaker)}
                            </span>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {formatTime(segment.start_time)} - {formatTime(segment.end_time)}
                            </span>
                            {onInsertText && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => handleInsertVideoSegment(segment)}
                                title="Add video segment to timeline"
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm">{segment.text}</p>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="text-sm whitespace-pre-wrap select-text">
                    {file.analysisData.transcript?.full_text || 
                     (typeof file.analysisData.transcript === 'string' 
                       ? file.analysisData.transcript 
                       : 'Transcript available')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Preview, Analysis & Comments Dialogs */}
        <PreviewDialog file={file} open={showPreview} onOpenChange={setShowPreview} onTranscriptEdit={onTranscriptEdit} onSpeakerEdit={onSpeakerEdit} />
        <AnalysisDialog file={file} open={showAnalysis} onOpenChange={setShowAnalysis} onInsertText={onInsertText} />
        <CommentsDialog file={file} open={showComments} onOpenChange={setShowComments} />
      </>
    );
  }

  return (
    <>
      <div
        draggable={!!onAddToTimeline}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'copy';
          e.dataTransfer.setData('fileId', file.id);
          onDragStart();
        }}
        onClick={() => { if (!onAddToTimeline) onSelect(); }}
        className={`relative group ${onAddToTimeline ? 'cursor-move' : 'cursor-pointer'} border rounded-lg overflow-hidden transition-all ${
          isSelected
            ? 'border-primary ring-2 ring-primary/20'
            : 'border-border hover:border-primary/50'
        }`}
      >
      {/* Selection Checkbox */}
      <div className="absolute top-2 left-2 z-10">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onSelect}
          className="bg-background/80 backdrop-blur-sm"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {/* Analysis Indicators & Status */}
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        {/* Background Processing Status */}
        {file.processingStatus && (
          <div className="flex items-center gap-1">
            {file.processingStatus === 'starting' && (
              <div className="animate-spin rounded-full h-3 w-3 border border-blue-300 border-t-blue-600" title="Starting background removal..." />
            )}
            {file.processingStatus === 'processing' && (
              <div className="animate-spin rounded-full h-3 w-3 border border-green-300 border-t-green-600" title="Removing background..." />
            )}
            {file.processingStatus === 'failed' && (
              <div className="rounded-full h-3 w-3 bg-red-500" title="Background removal failed" />
            )}
          </div>
        )}
        {/* Analysis Status Spinner */}
        {file.analysisStatus && (
          <div className="flex items-center gap-1">
            {file.analysisStatus === 'starting' && (
              <div className="animate-spin rounded-full h-3 w-3 border border-purple-300 border-t-purple-600" title="Starting analysis..." />
            )}
            {file.analysisStatus === 'processing' && (
              <div className="animate-spin rounded-full h-3 w-3 border border-yellow-300 border-t-yellow-600" title="Analyzing video..." />
            )}
            {file.analysisStatus === 'failed' && (
              <div className="rounded-full h-3 w-3 bg-red-500" title="Analysis failed" />
            )}
          </div>
        )}
        {/* Analysis Data Indicators */}
        {hasAnalysis && (
          <>
            {file.analysisData?.transcript && (
              <div className="w-2 h-2 rounded-full bg-green-500" title="Transcript available" />
            )}
            {file.analysisData?.background && (
              <div className="w-2 h-2 rounded-full bg-purple-500" title="Background analyzed" />
            )}
          </>
        )}
      </div>

      {/* Casting Data Indicators - Top Right */}
      {(file.liked || (file.comments && file.comments.length > 0)) && (
        <div className="absolute top-8 right-2 z-10 flex flex-col gap-1">
          {file.liked && (
            <div className="flex items-center gap-1 bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full text-xs font-medium">
              <Heart className="w-2.5 h-2.5 fill-current" />
            </div>
          )}
          {file.comments && file.comments.length > 0 && (
            <button 
              className="flex items-center gap-1 bg-blue-100 hover:bg-blue-200 text-blue-700 px-1.5 py-0.5 rounded-full text-xs font-medium transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setShowComments(true);
              }}
              title="View comments"
            >
              <MessageCircle className="w-2.5 h-2.5" />
              <span>{file.comments.length}</span>
            </button>
          )}
        </div>
      )}

      {/* Hover Actions - positioned at bottom of thumbnail area */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          size="sm"
          variant="secondary"
          className="h-6 w-6 p-0 shadow-md"
          onClick={(e) => {
            e.stopPropagation();
            setShowPreview(true);
          }}
          title="Preview"
        >
          <Eye className="h-3 w-3" />
        </Button>
        {onFileRemove && (
          <Button
            size="sm"
            variant="destructive"
            className="h-6 w-6 p-0 shadow-md"
            onClick={(e) => {
              e.stopPropagation();
              onFileRemove(file.id);
            }}
            title="Remove from project bin"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Thumbnail */}
      {renderThumbnail()}

      {/* File Info */}
      <div className="p-1.5 bg-background">
        <p className="text-xs font-medium truncate leading-tight" title={file.name}>
          {file.name}
        </p>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {file.duration > 0 && <span>{Math.round(file.duration)}s</span>}
          {(file as any).aspectRatio && (
            <span className={(file as any).aspectRatio === '9:16' ? 'text-purple-600' : 'text-blue-600'} title={`Aspect ratio: ${(file as any).aspectRatio}`}>
              {(file as any).aspectRatio === '9:16' ? '📱' : '🖥️'}
            </span>
          )}
          {file.hasGreenScreen && <span className="text-green-600">🟢</span>}
          {(file as any).linkedMaskId && <span className="text-purple-600" title="Luma matte available">◐</span>}
          {file.layerType && file.layerType !== 'main' && file.layerType !== 'mask' && (
            <span className={file.layerType === 'background' ? 'text-purple-600' : 'text-blue-600'}>
              {file.layerType === 'background' ? '🎨' : '🎵'}
            </span>
          )}
          {(file.linkedAudioId || file.linkedVideoId) && <span className="text-green-600">🔗</span>}
        </div>
      </div>

      {/* Add Button - dropdown for green screen, luma matte, or files with transcript */}
      {onAddToTimeline && (
        <div className="absolute bottom-10 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
          {(() => {
            const hasTranscript = hasAnalysis && file.analysisData?.transcript?.segments?.length > 0;
            const hasLumaMask = !!(file as any).linkedMaskId;
            const hasGreenScreen = !!file.hasGreenScreen;
            const showDropdown = hasTranscript || hasLumaMask || hasGreenScreen;

            if (showDropdown) {
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="default"
                      className="h-6 px-1.5 shadow-md gap-0.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Plus className="h-3 w-3" />
                      <ChevronDown className="h-2.5 w-2.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    {/* Green screen option */}
                    {hasGreenScreen && (
                      <DropdownMenuItem onClick={() => onAddToTimeline()}>
                        <span className="text-green-600 mr-2">🟢</span>
                        With Green Screen
                      </DropdownMenuItem>
                    )}
                    {/* Luma matte options */}
                    {hasLumaMask && (
                      <DropdownMenuItem onClick={() => {
                        if (onAddToTimelineWithOptions) {
                          onAddToTimelineWithOptions({ includeMask: true });
                        } else {
                          onAddToTimeline();
                        }
                      }}>
                        <Layers className="h-3.5 w-3.5 mr-2 text-purple-600" />
                        With Luma Matte
                      </DropdownMenuItem>
                    )}
                    {/* Video only option - when either green screen or luma mask exists */}
                    {(hasLumaMask || hasGreenScreen) && (
                      <DropdownMenuItem onClick={() => {
                        if (onAddToTimelineWithOptions) {
                          onAddToTimelineWithOptions({ videoOnly: true });
                        } else {
                          onAddToTimeline();
                        }
                      }}>
                        <PlaySquare className="h-3.5 w-3.5 mr-2" />
                        Original Video Only
                      </DropdownMenuItem>
                    )}
                    {/* Standard full video option when no processing */}
                    {!hasLumaMask && !hasGreenScreen && (
                      <DropdownMenuItem onClick={() => onAddToTimeline()}>
                        <PlaySquare className="h-3.5 w-3.5 mr-2" />
                        Full Video
                      </DropdownMenuItem>
                    )}
                    {/* Transcript segment selection */}
                    {hasTranscript && (
                      <DropdownMenuItem onClick={() => setShowPreview(true)}>
                        <Scissors className="h-3.5 w-3.5 mr-2" />
                        Select Sections...
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }

            return (
              <Button
                size="sm"
                variant="default"
                className="h-6 w-6 p-0 shadow-md"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToTimeline();
                }}
                title="Add to timeline"
              >
                <Plus className="h-3 w-3" />
              </Button>
            );
          })()}
        </div>
      )}

      {/* Preview, Analysis & Comments Dialogs */}
      <PreviewDialog file={file} open={showPreview} onOpenChange={setShowPreview} onInsertText={onInsertText} onTranscriptEdit={onTranscriptEdit} onSpeakerEdit={onSpeakerEdit} />
      <AnalysisDialog file={file} open={showAnalysis} onOpenChange={setShowAnalysis} onInsertText={onInsertText} />
      <CommentsDialog file={file} open={showComments} onOpenChange={setShowComments} />
    </div>
    </>
  );
};

// Preview Dialog Component - now includes transcript segments for adding to timeline
const PreviewDialog: React.FC<{
  file: ProjectFile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsertText?: (text: string, file?: ProjectFile, startTime?: number, endTime?: number, referenceText?: string) => void;
  onTranscriptEdit?: (fileId: string, segmentIndex: number, newText: string) => void;
  onSpeakerEdit?: (fileId: string, segmentIndex: number, newSpeaker: string) => void;
}> = ({ file, open, onOpenChange, onInsertText, onTranscriptEdit, onSpeakerEdit }) => {
  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);
  const [currentSegment, setCurrentSegment] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // Track edited transcript text per segment (keyed by index)
  const [editedTexts, setEditedTexts] = useState<{[key: number]: string}>({});

  const { analysisData } = file;
  const hasTranscript = analysisData?.transcript?.segments?.length > 0;

  // Get the text for a segment (edited or original)
  const getSegmentText = (segment: any, index: number): string => {
    return editedTexts[index] !== undefined ? editedTexts[index] : segment.text;
  };

  // Update edited text for a segment (local state + persist to file)
  const handleTextEdit = (index: number, newText: string) => {
    setEditedTexts(prev => ({ ...prev, [index]: newText }));
  };

  // Persist edit to source file when user finishes editing
  const handleTextBlur = (index: number) => {
    const editedText = editedTexts[index];
    if (editedText !== undefined && onTranscriptEdit) {
      onTranscriptEdit(file.id, index, editedText);
    }
  };

  // Helper to detect presigned S3 URLs (which can expire)
  const isPresignedUrl = (url: string): boolean => {
    if (!url) return false;
    return url.includes('AWSAccessKeyId=') ||
           url.includes('X-Amz-Signature=') ||
           url.includes('Expires=');
  };

  // Construct video URL - try multiple sources
  // IMPORTANT: Presigned URLs expire! Always prefer proxy URLs when possible
  const getVideoUrl = () => {
    if ((file as any).greenScreenUrl) return (file as any).greenScreenUrl;

    const s3Url = (file as any).s3Url;
    const s3Key = (file as any).s3Key;

    // If we have an s3Key and the s3Url is a presigned URL, use proxy instead
    // Presigned URLs expire after ~7 days, proxy URLs never expire
    if (s3Key && (!s3Url || isPresignedUrl(s3Url))) {
      return `/api/s3/${s3Key}`;
    }

    if (s3Url) return s3Url;
    if ((file as any).url) return (file as any).url;
    // Fallback: construct proxy URL from s3Key
    if (s3Key) return `/api/s3/${s3Key}`;
    return null;
  };
  const videoUrl = getVideoUrl();

  // Insert video segment with auto-generated enhancement prompt
  const handleInsertVideoSegment = (segment: any, index: number) => {
    if (onInsertText && segment) {
      const startTime = segment.start_time;
      const endTime = segment.end_time;
      // Use edited text if available, otherwise use original
      const text = getSegmentText(segment, index);

      const isGreenScreen = (file as any).hasGreenScreen;
      const enhancementPrompt = isGreenScreen ? `chromaKey #97eca9` : '';

      onInsertText(enhancementPrompt, file, startTime, endTime, text);
      onOpenChange(false);
    }
  };

  // Preview video segment
  const handlePreviewSegment = (segment: any) => {
    if (videoRef && segment) {
      setCurrentSegment(segment);
      videoRef.currentTime = segment.start_time;
      videoRef.play().then(() => {
        setIsPlaying(true);

        const checkTime = () => {
          if (videoRef.currentTime >= segment.end_time) {
            videoRef.pause();
            setIsPlaying(false);
          } else if (isPlaying) {
            requestAnimationFrame(checkTime);
          }
        };
        requestAnimationFrame(checkTime);
      }).catch(err => console.error('Failed to play video:', err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={hasTranscript ? "max-w-4xl max-h-[90vh]" : "max-w-3xl"}>
        <DialogHeader>
          <DialogTitle>{file.name}</DialogTitle>
          <DialogDescription>
            {file.fileType} • {Math.round(file.duration)}s
            {hasTranscript && ` • ${analysisData.transcript.segments.length} segments`}
          </DialogDescription>
        </DialogHeader>

        {/* Video/Audio Preview */}
        <div className={hasTranscript ? "aspect-video bg-muted rounded-lg" : "aspect-video bg-muted rounded-lg flex items-center justify-center"}>
          {file.fileType === 'video' && videoUrl ? (
            <div className="w-full h-full">
              {(file as any).hasGreenScreen && (
                <div className="bg-green-900/20 border border-green-700/50 px-3 py-2 rounded mb-2">
                  <p className="text-xs text-green-600">
                    ✅ Green screen video with merged audio - ready for compositing
                  </p>
                </div>
              )}
              <video
                ref={setVideoRef}
                src={videoUrl}
                controls
                className="w-full h-full object-contain"
                poster={file.thumbnailUrl}
                onEnded={() => setIsPlaying(false)}
              />
              {currentSegment && hasTranscript && (
                <div className="mt-2 p-2 bg-muted rounded text-sm">
                  <span className="font-medium text-primary">{formatSpeaker(currentSegment.speaker)}</span>
                  <span className="text-muted-foreground ml-2">
                    {formatTime(currentSegment.start_time)} - {formatTime(currentSegment.end_time)}
                  </span>
                  {isPlaying && <span className="text-green-500 ml-2">▶ Playing</span>}
                </div>
              )}
            </div>
          ) : file.fileType === 'audio' && ((file as any).s3Url || (file as any).url || (file as any).s3Key) ? (
            <div className="flex flex-col items-center justify-center space-y-4">
              <Music className="w-16 h-16 text-muted-foreground" />
              <audio src={getVideoUrl() || (file as any).url || ''} controls className="w-full max-w-md" />
              <p className="text-sm text-muted-foreground">
                {(file as any).linkedVideoId ? 'Original audio from green screen video' : 'Audio file'}
              </p>
            </div>
          ) : file.fileType === 'video' && file.thumbnailUrl && file.thumbnailUrl.startsWith('data:') ? (
            <img src={file.thumbnailUrl} alt={file.name} className="w-full h-full object-contain" />
          ) : file.fileType === 'image' && file.thumbnailUrl ? (
            <img src={file.thumbnailUrl} alt={file.name} className="w-full h-full object-contain" />
          ) : (
            <div className="text-center text-muted-foreground">
              <p>Preview not available</p>
              <p className="text-xs mt-1">Full playback coming soon</p>
            </div>
          )}
        </div>

        {/* Transcript Segments */}
        {hasTranscript && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-4 w-4 text-green-500" />
              <h3 className="font-medium text-sm">Transcript Segments</h3>
              <span className="text-xs text-muted-foreground">
                (edit text for captions, click + to add)
              </span>
            </div>
            <ScrollArea className="h-[200px] pr-4">
              <div className="space-y-2">
                {analysisData.transcript.segments.map((segment: any, index: number) => {
                  const isCurrentSegment = currentSegment && currentSegment === segment;
                  return (
                    <div
                      key={`${index}-${segment.speaker}`}
                      className={`p-2 rounded-lg group transition-colors ${
                        isCurrentSegment && isPlaying
                          ? 'bg-green-100 dark:bg-green-900/20 ring-1 ring-green-500'
                          : 'bg-muted hover:bg-muted/80'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        {onSpeakerEdit && (analysisData?.transcript?.speaker_count || 0) > 1 ? (
                          <Select
                            value={segment.speaker || 'spk_0'}
                            onValueChange={(value) => onSpeakerEdit(file.id, index, value)}
                          >
                            <SelectTrigger className="h-6 w-auto min-w-0 gap-1 px-1.5 py-0 text-xs font-semibold text-primary border-transparent hover:border-border bg-transparent">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: analysisData.transcript.speaker_count }, (_, i) => (
                                <SelectItem key={i} value={`spk_${i}`} className="text-xs">
                                  Speaker {i + 1}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-xs font-semibold text-primary">
                            {formatSpeaker(segment.speaker)}
                          </span>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {formatTime(segment.start_time)} - {formatTime(segment.end_time)}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handlePreviewSegment(segment)}
                            title="Preview this segment"
                          >
                            <Play className="h-3 w-3" />
                          </Button>
                          {onInsertText && (
                            <Button
                              size="sm"
                              variant="default"
                              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleInsertVideoSegment(segment, index)}
                              title="Add segment to timeline"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <textarea
                        className="text-sm w-full bg-transparent border border-transparent hover:border-border focus:border-primary focus:ring-1 focus:ring-primary rounded px-1 py-0.5 resize-none min-h-[2.5rem]"
                        value={getSegmentText(segment, index)}
                        onChange={(e) => handleTextEdit(index, e.target.value)}
                        onBlur={() => handleTextBlur(index)}
                        rows={2}
                        title="Edit transcript text for captions - saves to file when you click away"
                      />
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Analysis Dialog Component
const AnalysisDialog: React.FC<{
  file: ProjectFile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInsertText?: (text: string, file?: ProjectFile, startTime?: number, endTime?: number, referenceText?: string) => void;
}> = ({ file, open, onOpenChange, onInsertText }) => {
  const { analysisData } = file;
  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);
  const [currentSegment, setCurrentSegment] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Helper to detect presigned S3 URLs (which can expire)
  const isPresignedUrl = (url: string): boolean => {
    if (!url) return false;
    return url.includes('AWSAccessKeyId=') ||
           url.includes('X-Amz-Signature=') ||
           url.includes('Expires=');
  };

  // Construct video URL - prefer proxy URLs over presigned URLs
  const getVideoUrl = () => {
    if ((file as any).greenScreenUrl) return (file as any).greenScreenUrl;
    const s3Url = (file as any).s3Url;
    const s3Key = (file as any).s3Key;
    if (s3Key && (!s3Url || isPresignedUrl(s3Url))) {
      return `/api/s3/${s3Key}`;
    }
    if (s3Url) return s3Url;
    if ((file as any).url) return (file as any).url;
    if (s3Key) return `/api/s3/${s3Key}`;
    return null;
  };

  // Insert video segment with auto-generated enhancement prompt
  const handleInsertVideoSegment = (segment: any) => {
    if (onInsertText && segment) {
      // Generate auto-enhancement prompt
      const startTime = segment.start_time;
      const endTime = segment.end_time;
      const text = segment.text;
      const speaker = formatSpeaker(segment.speaker);
      
      // Create prompt without quoted text for LLM, but show text separately for user reference
      // Check if this is a green screen video and add chromaKey instruction
      const isGreenScreen = (file as any).hasGreenScreen;
      const enhancementPrompt = isGreenScreen 
        ? `chromaKey #97eca9`
        : '';
      
      // Insert only the enhancement prompt (trim instruction) - reference text is shown separately in UI
      onInsertText(
        enhancementPrompt,
        file, 
        startTime, 
        endTime,
        text // Pass the original transcript text as reference
      );
      
      onOpenChange(false); // Close modal after inserting
    }
  };

  // Preview video segment
  const handlePreviewSegment = (segment: any) => {
    if (videoRef && segment) {
      setCurrentSegment(segment);
      videoRef.currentTime = segment.start_time;
      videoRef.play().then(() => {
        setIsPlaying(true);
        
        // Stop playback at segment end
        const checkTime = () => {
          if (videoRef.currentTime >= segment.end_time) {
            videoRef.pause();
            setIsPlaying(false);
          } else if (isPlaying) {
            requestAnimationFrame(checkTime);
          }
        };
        requestAnimationFrame(checkTime);
      }).catch(err => console.error('Failed to play video:', err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Analysis Results
            {file.analysisStatus && (
              <div className="flex items-center gap-1">
                {file.analysisStatus === 'starting' && (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border border-purple-300 border-t-purple-600" />
                    <span className="text-xs text-purple-600 font-medium">Starting...</span>
                  </>
                )}
                {file.analysisStatus === 'processing' && (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border border-yellow-300 border-t-yellow-600" />
                    <span className="text-xs text-yellow-600 font-medium">Analyzing...</span>
                  </>
                )}
                {file.analysisStatus === 'failed' && (
                  <>
                    <div className="rounded-full h-3 w-3 bg-red-500" />
                    <span className="text-xs text-red-600 font-medium">Failed</span>
                  </>
                )}
              </div>
            )}
          </DialogTitle>
          <DialogDescription>{file.name}</DialogDescription>
        </DialogHeader>
        
        {/* Video Preview Section */}
        {file.fileType === 'video' && ((file as any).greenScreenUrl || (file as any).s3Url) && (
          <div className="mb-4">
            {/* Show note for green screen videos */}
            {(file as any).hasGreenScreen && (
              <div className="bg-green-900/20 border border-green-700/50 px-3 py-2 rounded mb-2">
                <p className="text-xs text-green-600">
                  ✅ Green screen video with merged audio - ready for compositing
                </p>
              </div>
            )}
            <video
              ref={setVideoRef}
              src={(file as any).greenScreenUrl || getVideoUrl() || ''} // Use green screen with merged audio if available
              className="w-full h-48 object-contain bg-black rounded"
              controls={false}
              onEnded={() => setIsPlaying(false)}
            />
            {currentSegment && (
              <div className="mt-2 p-2 bg-muted rounded text-sm">
                <span className="font-medium text-primary">{formatSpeaker(currentSegment.speaker)}</span>
                <span className="text-muted-foreground ml-2">
                  {formatTime(currentSegment.start_time)} - {formatTime(currentSegment.end_time)}
                </span>
                {isPlaying && <span className="text-green-500 ml-2">▶ Playing</span>}
              </div>
            )}
          </div>
        )}
        
        <ScrollArea className="h-[50vh] pr-4">
          <div className="space-y-6">
            {/* Background Detection */}
            {analysisData?.background && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Palette className="h-4 w-4 text-purple-500" />
                  <h3 className="font-medium">Background Detection</h3>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm">
                    {analysisData.background?.type || analysisData.background}
                    {analysisData.background?.confidence && 
                      ` (${Math.round(analysisData.background.confidence * 100)}% confidence)`}
                  </p>
                </div>
              </div>
            )}

            {/* Transcript */}
            {analysisData?.transcript && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-green-500" />
                  <h3 className="font-medium">Transcript</h3>
                  {analysisData.transcript?.speaker_count && (
                    <span className="text-xs text-muted-foreground">
                      ({analysisData.transcript.speaker_count} speaker{analysisData.transcript.speaker_count > 1 ? 's' : ''})
                    </span>
                  )}
                </div>

                {/* Show segments with speakers and timestamps */}
                {analysisData.transcript?.segments ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {analysisData.transcript.segments.map((segment: any, index: number) => {
                      const isCurrentSegment = currentSegment && currentSegment === segment;
                      return (
                        <div 
                          key={index} 
                          className={`p-2 rounded-lg group transition-colors ${
                            isCurrentSegment && isPlaying 
                              ? 'bg-green-100 dark:bg-green-900/20 ring-1 ring-green-500' 
                              : 'bg-muted'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-primary">
                              {formatSpeaker(segment.speaker)}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {formatTime(segment.start_time)} - {formatTime(segment.end_time)}
                              </span>
                              {file.fileType === 'video' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => handlePreviewSegment(segment)}
                                  title="Preview this segment"
                                >
                                  <Play className="h-3 w-3" />
                                </Button>
                              )}
                              {onInsertText && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => handleInsertVideoSegment(segment)}
                                  title="Add video segment to timeline"
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <p className="text-sm">{segment.text}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="text-sm whitespace-pre-wrap">
                      {analysisData.transcript?.full_text || 
                       (typeof analysisData.transcript === 'string' 
                         ? analysisData.transcript 
                         : 'Transcript available')}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!analysisData?.background && !analysisData?.transcript && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No analysis data available</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// Comments Dialog Component
const CommentsDialog: React.FC<{
  file: ProjectFile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ file, open, onOpenChange }) => {
  const comments = file.comments || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-blue-500" />
            Comments ({comments.length})
          </DialogTitle>
          <DialogDescription>
            Comments from casting review for {file.name}
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-4">
            {comments.length > 0 ? (
              comments.map((comment: any, index: number) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-blue-700">
                          {comment.author?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {comment.author || 'Unknown User'}
                        </p>
                        {comment.timestamp && (
                          <p className="text-xs text-muted-foreground">
                            {new Date(comment.timestamp).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">
                    {comment.text || comment.comment || comment.content || 'No comment text'}
                  </p>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No comments available</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
